/**
 * ActivityFeed - Log entries list
 *
 * Usage:
 *   <ActivityFeed
 *     title="Recent Activity"
 *     data={{
 *       items: [
 *         { id: '1', title: 'Morning run', subtitle: '5.2km in 28:34', time: '2h ago', icon: '🏃' },
 *         { id: '2', title: 'Pushed to main', subtitle: 'feat: add blocks', time: '4h ago', icon: '💻' },
 *       ]
 *     }}
 *   />
 */

import type { BlockProps, ActivityFeedData } from './types';
import './blocks.css';

export interface ActivityFeedProps extends BlockProps<ActivityFeedData> {
  /** Maximum items to display */
  maxItems?: number;
  /** Item click handler */
  onItemClick?: (item: ActivityFeedData['items'][0]) => void;
}

const defaultIcons: Record<string, string> = {
  commit: '💻',
  push: '📤',
  pr: '🔀',
  issue: '📋',
  run: '🏃',
  workout: '💪',
  sleep: '😴',
  meal: '🍽️',
  order: '📦',
  email: '📧',
  meeting: '📅',
  default: '📌',
};

export function ActivityFeed({
  data,
  title,
  maxItems,
  onItemClick,
  className,
}: ActivityFeedProps) {
  const { items = [] } = data ?? {};

  if (!items || items.length === 0) {
    return (
      <div className={`activity-feed ${className || ''}`}>
        {title && <div className="activity-feed-title">{title}</div>}
        <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--color-text-faint)' }}>
          No activity yet
        </div>
      </div>
    );
  }

  const displayItems = maxItems ? items.slice(0, maxItems) : items;

  const getIcon = (item: ActivityFeedData['items'][0]) => {
    if (item.icon) return item.icon;
    if (item.type && defaultIcons[item.type]) return defaultIcons[item.type];
    return defaultIcons.default;
  };

  return (
    <div className={`activity-feed ${className || ''}`}>
      {title && <div className="activity-feed-title">{title}</div>}

      <ul className="activity-feed-list">
        {displayItems.map((item) => (
          <li
            key={item.id}
            className="activity-feed-item"
            onClick={onItemClick ? () => onItemClick(item) : undefined}
            onKeyDown={onItemClick ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onItemClick(item);
              }
            } : undefined}
            role={onItemClick ? 'button' : undefined}
            tabIndex={onItemClick ? 0 : undefined}
            aria-label={onItemClick ? `${item.title}${item.subtitle ? `, ${item.subtitle}` : ''}` : undefined}
            style={{ cursor: onItemClick ? 'pointer' : undefined }}
          >
            <span className="activity-feed-icon">{getIcon(item)}</span>

            <div className="activity-feed-content">
              <div className="activity-feed-item-title">{item.title}</div>
              {item.subtitle && (
                <div className="activity-feed-item-subtitle">{item.subtitle}</div>
              )}
            </div>

            <span className="activity-feed-time">{item.time}</span>
          </li>
        ))}
      </ul>

      {maxItems && items.length > maxItems && (
        <div style={{
          padding: '8px 16px',
          textAlign: 'center',
          fontSize: '12px',
          color: 'var(--color-text-muted)',
          borderTop: '1px solid var(--color-border)',
        }}>
          +{items.length - maxItems} more
        </div>
      )}
    </div>
  );
}

export default ActivityFeed;
